/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import * as stagesCase from "../../../Stages/StagesCase";
import personFields from "../modules/personFields";
import { dateFieldDefault, instituteReferenceDefault } from "../../../validationRules";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import _ from "lodash";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { userHasOneOfTheseRoles } from "../../../backofficeUserService";
import { BackofficeUser, Roles } from "../../../../types/BackofficeUser";
import { abstractLitigationStageFormDefinition } from "../abstractLitigationCase/stageFormDefinition";
import { ProductUnion } from "../../../../types/ProductUnion";
import { GeneralCivilLaw } from "../../../../types/Products/GeneralCivilLaw";

export function stageFormDefinition(
  product: GeneralCivilLaw,
  currentUser?: BackofficeUser
): ReturnType<FormDefinition<GeneralCivilLaw>> {
  const createdFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    ...personFields({ path: `${product.productClassName}.client`, showDiverseGenderOption: true }),
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
  ];

  const dataCompletedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.objectivesClient`,
      isDisabled: () => true,
      isHidden: ({ product }) => product.caseCategory !== "enforceClaim",
      additionalProps: {
        multiple: true,
        fullWidth: true,
      },
      options: () => [
        { value: "payment" },
        { value: "injunction" },
        { value: "removal" },
        { value: "handover" },
        { value: "other" },
      ],
    },
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_DATA_COMPLETED],
    {
      type: "ValidatorNumberField",
      path: `${product.productClassName}.claimedAmountClient`,
      isHidden: ({ product }) =>
        product.caseCategory !== "enforceClaim" || product.objectivesClient.indexOf("payment") === -1,
      numberType: "float",
      fieldType: "money",
    },
  ];

  const dataLawFirmCompletedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [...dataCompletedFields];

  const checkedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ExpandableTextField",
      path: `${product.productClassName}.personalTextMessage`,
      isDisabled: () => true,
      additionalProps: {
        multiline: true,
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_CHECKED],
  ];

  const inProgressFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_IN_PROGRESS],
  ];

  const communicationSentFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: is this text correct necessary in generalCivilLaw
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT],
  ];

  const reactionReceivedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: is this text correct necessary in generalCivilLaw
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED],
  ];

  const actionRequiredFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_ACTION_REQUIRED],
  ];

  const prepareLawsuitFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: is this text correct necessary in generalCivilLaw
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_PREPARE_LAWSUIT],
  ];

  const lawsuitFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT],
  ];

  const lawsuitCompletedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: is this text correct necessary in generalCivilLaw
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
    },
    {
      type: "ValidatorTextField",
      path: `${product.productClassName}.courtReference`,
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      validators: dateFieldDefault,
      path: `${product.productClassName}.judgementEebSentDate`,
    },
    {
      type: "ValidatorDateField",
      path: `${product.productClassName}.promulgationOfJudgementDate`,
      validators: dateFieldDefault,
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_LAWSUIT_COMPLETED],
  ];

  const completedFields: FormElementPropsWithoutProduct<GeneralCivilLaw>[] = [
    {
      type: "ValidatorSelect",
      path: `${product.productClassName}.hasMonitoringBilling`,
      isHidden: ({ product }: { product: ProductUnion }) =>
        !_.includes([2], product.caseInsurance.insurer?.id) || product.productClassName === "dataLeakContract", // TODO: is this text correct necessary in generalCivilLaw
      options: () => trueFalseDefaultOptions(`${product.productClassName}.hasMonitoringBilling`),
      isDisabled: () => {
        return !currentUser || !userHasOneOfTheseRoles(currentUser, [Roles.accounting, Roles.admin]);
      },
    },
    ...abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_COMPLETED],
  ];

  return {
    [stagesCase.STAGE_CREATED]: createdFields,
    [stagesCase.STAGE_DATA_COMPLETED]: dataCompletedFields,
    [stagesCase.STAGE_WAITING_FOR_FEEDBACK]: dataCompletedFields,
    [stagesCase.STAGE_DATA_COMPLETED_LAWFIRM]: dataLawFirmCompletedFields,
    [stagesCase.STAGE_CHECKED]: checkedFields,
    [stagesCase.STAGE_IN_PROGRESS]: inProgressFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_COMMUNICATION_SENT]: communicationSentFields,
    [stagesCase.STAGE_EXTRAJUDICIAL_REACTION_RECEIVED]: reactionReceivedFields,
    [stagesCase.STAGE_ACTION_REQUIRED]: actionRequiredFields,
    [stagesCase.STAGE_PREPARE_LAWSUIT]: prepareLawsuitFields,
    [stagesCase.STAGE_LAWSUIT]: lawsuitFields,
    [stagesCase.STAGE_TRIAL]: lawsuitFields,
    [stagesCase.STAGE_EVALUATE_JUDGEMENT]:
      abstractLitigationStageFormDefinition(product)[stagesCase.STAGE_EVALUATE_JUDGEMENT],
    [stagesCase.STAGE_LAWSUIT_COMPLETED]: lawsuitCompletedFields,
    [stagesCase.STAGE_COMPLETED]: completedFields,
  };
}
